import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Shield,
  ShieldCheck,
  ShieldOff,
  KeyRound,
  Mail,
  RefreshCw,
  LogOut,
  AlertTriangle,
  Loader2,
  ArrowRight,
  Check,
} from 'lucide-react';
import {
  GlassCard,
  GlassCardContent,
  GlassCardDescription,
  GlassCardHeader,
  GlassCardTitle,
} from '@easyflow/ui-master/components/ui/glass-card';
import { Button } from '@easyflow/ui-master/components/ui/button';
import { Input } from '@easyflow/ui-master/components/ui/input';
import { Label } from '@easyflow/ui-master/components/ui/label';
import { Alert, AlertDescription } from '@easyflow/ui-master/components/ui/alert';
import { TwoFactorStatusBadge } from '@easyflow/ui-master/components/ui/two-factor-status-badge';
import { TotpQrSetup } from '@easyflow/ui-master/components/ui/totp-qr-setup';
import { TotpCodeInput } from '@easyflow/ui-master/components/ui/totp-code-input';
import { BackupCodesViewer } from '@easyflow/ui-master/components/ui/backup-codes-viewer';
import { SessionRow } from '@easyflow/ui-master/components/ui/session-row';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@easyflow/ui-master/components/ui/dialog';
import apiClient from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import type {
  MasterRefreshTokenInfo,
  MasterTwoFactorSetupResponse,
  MasterTwoFactorVerifyResponse,
  MasterTwoFactorEnableRequest,
  MasterTwoFactorDisableRequest,
  MasterTwoFactorRegenerateBackupRequest,
  MasterTwoFactorRequirementResponse,
} from '@easyflow/shared';

interface MeStatus {
  totpEnabled: boolean;
  email: string;
}

type TotpSetupData = MasterTwoFactorSetupResponse;

type WizardStep = 0 | 1 | 2;

interface PasswordStrength {
  score: 0 | 1 | 2 | 3 | 4;
  label: string;
  className: string;
}

function evaluatePasswordStrength(pwd: string): PasswordStrength {
  let score = 0;
  if (pwd.length >= 8) score++;
  if (pwd.length >= 12) score++;
  if (/[A-Z]/.test(pwd) && /[a-z]/.test(pwd)) score++;
  if (/\d/.test(pwd) && /[^A-Za-z0-9]/.test(pwd)) score++;
  const clamped = Math.min(score, 4) as 0 | 1 | 2 | 3 | 4;
  const labels: Record<0 | 1 | 2 | 3 | 4, string> = {
    0: 'Molto debole',
    1: 'Debole',
    2: 'Discreta',
    3: 'Buona',
    4: 'Forte',
  };
  const classes: Record<0 | 1 | 2 | 3 | 4, string> = {
    0: 'bg-red-500 w-1/5',
    1: 'bg-red-500 w-2/5',
    2: 'bg-amber-500 w-3/5',
    3: 'bg-lime-500 w-4/5',
    4: 'bg-green-600 w-full',
  };
  return { score: clamped, label: labels[clamped], className: classes[clamped] };
}

export default function MasterSecuritySettings(): JSX.Element {
  const { user } = useAuth();
  const { toast } = useToast();

  // ---- 2FA state -----------------------------------------------------------
  const [meStatus, setMeStatus] = useState<MeStatus | null>(null);
  const [loadingStatus, setLoadingStatus] = useState<boolean>(true);
  const [twoFactorRequired, setTwoFactorRequired] = useState<boolean>(false);
  const [setupData, setSetupData] = useState<TotpSetupData | null>(null);
  const [wizardStep, setWizardStep] = useState<WizardStep>(0);
  const [wizardCode, setWizardCode] = useState<string>('');
  const [wizardError, setWizardError] = useState<string | null>(null);
  const [wizardAck, setWizardAck] = useState<boolean>(false);
  const [wizardBusy, setWizardBusy] = useState<boolean>(false);

  // Disable 2FA
  const [disableOpen, setDisableOpen] = useState<boolean>(false);
  const [disablePassword, setDisablePassword] = useState<string>('');
  const [disableCode, setDisableCode] = useState<string>('');
  const [disableError, setDisableError] = useState<string | null>(null);
  const [disableBusy, setDisableBusy] = useState<boolean>(false);

  // Regenerate backup
  const [regenOpen, setRegenOpen] = useState<boolean>(false);
  const [regenCode, setRegenCode] = useState<string>('');
  const [regenError, setRegenError] = useState<string | null>(null);
  const [regenBusy, setRegenBusy] = useState<boolean>(false);
  const [regenResult, setRegenResult] = useState<string[] | null>(null);

  // ---- Password change (authenticated old/new/confirm) ---------------------
  const [currentPassword, setCurrentPassword] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [pwdBusy, setPwdBusy] = useState<boolean>(false);
  const [pwdError, setPwdError] = useState<string | null>(null);
  const [pwdSuccess, setPwdSuccess] = useState<boolean>(false);
  const [pwdResetEmailBusy, setPwdResetEmailBusy] = useState<boolean>(false);
  const [pwdResetEmailSent, setPwdResetEmailSent] = useState<boolean>(false);
  const pwdStrength = useMemo<PasswordStrength>(() => evaluatePasswordStrength(newPassword), [newPassword]);
  const pwdMismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;
  const pwdPolicyOk = useMemo<boolean>(() => (
    newPassword.length >= 8
    && /[A-Z]/.test(newPassword)
    && /[a-z]/.test(newPassword)
    && /\d/.test(newPassword)
    && /[^A-Za-z0-9]/.test(newPassword)
  ), [newPassword]);

  // ---- Sessions ------------------------------------------------------------
  const [sessions, setSessions] = useState<MasterRefreshTokenInfo[]>([]);
  const [loadingSessions, setLoadingSessions] = useState<boolean>(true);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [revokeAllBusy, setRevokeAllBusy] = useState<boolean>(false);

  const fetchStatus = useCallback(async (): Promise<void> => {
    setLoadingStatus(true);
    try {
      const res = await apiClient.get<{ totp_enabled?: boolean; email?: string }>('/master/me');
      if (res.success && res.data) {
        setMeStatus({
          totpEnabled: Boolean(res.data.totp_enabled),
          email: res.data.email ?? user?.email ?? '',
        });
      } else {
        // Fallback: deriva da JWT user
        setMeStatus({ totpEnabled: false, email: user?.email ?? '' });
      }
    } catch {
      setMeStatus({ totpEnabled: false, email: user?.email ?? '' });
    } finally {
      setLoadingStatus(false);
    }
  }, [user?.email]);

  const fetchSessions = useCallback(async (): Promise<void> => {
    if (!user?.id) return;
    setLoadingSessions(true);
    try {
      const res = await apiClient.get<{ user_id: string; sessions: MasterRefreshTokenInfo[] }>(
        `/master/master-users/${user.id}/sessions`,
      );
      if (res.success && res.data) {
        setSessions(res.data.sessions);
      }
    } catch (err) {
      toast({
        title: 'Errore',
        description: err instanceof Error ? err.message : 'Errore caricamento sessioni',
        variant: 'destructive',
      });
    } finally {
      setLoadingSessions(false);
    }
  }, [user?.id, toast]);

  const fetchTwoFaRequirement = useCallback(async (): Promise<void> => {
    try {
      const res = await apiClient.get<MasterTwoFactorRequirementResponse>('/master/2fa-requirement');
      if (res.success && res.data) {
        setTwoFactorRequired(res.data.twoFactorRequired);
      }
    } catch {
      // Best-effort: in caso di errore non mostriamo il banner di obbligo.
    }
  }, []);

  useEffect(() => {
    void fetchStatus();
    void fetchSessions();
    void fetchTwoFaRequirement();
  }, [fetchStatus, fetchSessions, fetchTwoFaRequirement]);

  // ---- 2FA wizard handlers -------------------------------------------------
  const startSetup = useCallback(async (): Promise<void> => {
    setWizardBusy(true);
    setWizardError(null);
    try {
      const res = await apiClient.post<MasterTwoFactorSetupResponse>('/master/2fa/setup', {});
      if (res.success && res.data) {
        setSetupData(res.data);
        setWizardStep(0);
        setWizardCode('');
        setWizardAck(false);
      } else {
        throw new Error(res.error ?? 'Errore avvio setup 2FA');
      }
    } catch (err) {
      toast({
        title: 'Errore',
        description: err instanceof Error ? err.message : 'Errore setup 2FA',
        variant: 'destructive',
      });
    } finally {
      setWizardBusy(false);
    }
  }, [toast]);

  const confirmEnable = useCallback(async (code: string): Promise<void> => {
    if (code.length !== 6) {
      setWizardError('Inserisci le 6 cifre');
      return;
    }
    setWizardBusy(true);
    setWizardError(null);
    try {
      const body: MasterTwoFactorEnableRequest = { code };
      const res = await apiClient.post<MasterTwoFactorVerifyResponse>('/master/2fa/enable', body);
      if (res.success) {
        setWizardStep(2);
      } else {
        setWizardError(res.error ?? 'Codice non valido');
        setWizardCode('');
      }
    } catch (err) {
      setWizardError(err instanceof Error ? err.message : 'Codice non valido');
      setWizardCode('');
    } finally {
      setWizardBusy(false);
    }
  }, []);

  const finishWizard = useCallback((): void => {
    setSetupData(null);
    setWizardStep(0);
    setWizardCode('');
    setWizardAck(false);
    setWizardError(null);
    void fetchStatus();
    toast({ title: '2FA attivo', description: 'Autenticazione a due fattori configurata correttamente.' });
  }, [fetchStatus, toast]);

  const cancelWizard = useCallback((): void => {
    setSetupData(null);
    setWizardStep(0);
    setWizardCode('');
    setWizardAck(false);
    setWizardError(null);
  }, []);

  const submitDisable = useCallback(async (): Promise<void> => {
    if (disablePassword.length === 0 || disableCode.length !== 6) {
      setDisableError('Password e codice TOTP (6 cifre) obbligatori');
      return;
    }
    setDisableBusy(true);
    setDisableError(null);
    try {
      const body: MasterTwoFactorDisableRequest = {
        password: disablePassword,
        code: disableCode,
      };
      const res = await apiClient.post<{ totpEnabled: false }>('/master/2fa/disable', body);
      if (res.success) {
        toast({ title: '2FA disattivato', description: 'Autenticazione a due fattori rimossa dal tuo account.' });
        setDisableOpen(false);
        setDisablePassword('');
        setDisableCode('');
        void fetchStatus();
      } else {
        setDisableError(res.error ?? 'Operazione fallita');
      }
    } catch (err) {
      setDisableError(err instanceof Error ? err.message : 'Operazione fallita');
    } finally {
      setDisableBusy(false);
    }
  }, [disablePassword, disableCode, toast, fetchStatus]);

  const submitRegenerate = useCallback(async (): Promise<void> => {
    if (regenCode.length !== 6) {
      setRegenError('Inserisci le 6 cifre del codice TOTP');
      return;
    }
    setRegenBusy(true);
    setRegenError(null);
    try {
      const body: MasterTwoFactorRegenerateBackupRequest = { code: regenCode };
      const res = await apiClient.post<{ backupCodes: string[] }>(
        '/master/2fa/regenerate-backup',
        body,
      );
      if (res.success && res.data) {
        setRegenResult(res.data.backupCodes);
        setRegenCode('');
      } else {
        setRegenError(res.error ?? 'Codice non valido');
      }
    } catch (err) {
      setRegenError(err instanceof Error ? err.message : 'Codice non valido');
    } finally {
      setRegenBusy(false);
    }
  }, [regenCode]);

  const closeRegen = useCallback((): void => {
    setRegenOpen(false);
    setRegenCode('');
    setRegenError(null);
    setRegenResult(null);
  }, []);

  // ---- Password change autenticato (old/new/confirm) ----------------------
  const submitPasswordChange = useCallback(async (): Promise<void> => {
    setPwdError(null);
    if (currentPassword.length === 0) {
      setPwdError('Inserisci la password corrente.');
      return;
    }
    if (!pwdPolicyOk) {
      setPwdError('La nuova password non soddisfa la policy (min 8, maiuscola, minuscola, numero, simbolo).');
      return;
    }
    if (pwdMismatch) {
      setPwdError('Le due password non coincidono.');
      return;
    }
    if (currentPassword === newPassword) {
      setPwdError('La nuova password deve essere diversa dalla precedente.');
      return;
    }
    setPwdBusy(true);
    try {
      const res = await apiClient.post('/master/change-password', {
        currentPassword,
        newPassword,
      });
      if (res.success) {
        setPwdSuccess(true);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        toast({
          title: 'Password aggiornata',
          description: 'Le altre sessioni attive sono state invalidate.',
        });
        await fetchSessions();
      } else {
        setPwdError(res.error ?? 'Errore cambio password');
      }
    } catch (err) {
      setPwdError(err instanceof Error ? err.message : 'Errore cambio password');
    } finally {
      setPwdBusy(false);
    }
  }, [currentPassword, newPassword, pwdPolicyOk, pwdMismatch, toast, fetchSessions]);

  // ---- Password reset via email (fallback "ho dimenticato la corrente") ---
  const requestPasswordReset = useCallback(async (): Promise<void> => {
    if (!meStatus?.email) return;
    setPwdResetEmailBusy(true);
    try {
      const res = await apiClient.post('/master/forgot-password', { email: meStatus.email });
      if (res.success) {
        setPwdResetEmailSent(true);
        toast({
          title: 'Email inviata',
          description: 'Controlla la tua casella per reimpostare la password.',
        });
      } else {
        throw new Error(res.error ?? 'Errore richiesta reset');
      }
    } catch (err) {
      toast({
        title: 'Errore',
        description: err instanceof Error ? err.message : 'Errore richiesta reset',
        variant: 'destructive',
      });
    } finally {
      setPwdResetEmailBusy(false);
    }
  }, [meStatus?.email, toast]);

  // ---- Sessions handlers ---------------------------------------------------
  const revokeSession = useCallback(async (sessionId: string): Promise<void> => {
    if (!user?.id) return;
    setRevokingId(sessionId);
    try {
      const res = await apiClient.post(`/master/master-users/${user.id}/revoke-sessions`, {
        session_id: sessionId,
        reason: 'Revoca singola sessione da pagina sicurezza personale',
      });
      if (res.success) {
        toast({ title: 'Sessione revocata' });
        await fetchSessions();
      } else {
        throw new Error(res.error ?? 'Errore revoca');
      }
    } catch (err) {
      toast({
        title: 'Errore',
        description: err instanceof Error ? err.message : 'Errore revoca sessione',
        variant: 'destructive',
      });
    } finally {
      setRevokingId(null);
    }
  }, [user?.id, fetchSessions, toast]);

  const revokeAll = useCallback(async (): Promise<void> => {
    if (!user?.id) return;
    setRevokeAllBusy(true);
    try {
      const res = await apiClient.post(`/master/master-users/${user.id}/revoke-sessions`, {
        reason: 'Revoca di tutte le altre sessioni da pagina sicurezza personale',
      });
      if (res.success) {
        toast({
          title: 'Sessioni revocate',
          description: 'Tutte le altre sessioni sono state chiuse. La sessione corrente potrebbe richiedere un nuovo accesso.',
        });
        await fetchSessions();
      } else {
        throw new Error(res.error ?? 'Errore revoca');
      }
    } catch (err) {
      toast({
        title: 'Errore',
        description: err instanceof Error ? err.message : 'Errore revoca sessioni',
        variant: 'destructive',
      });
    } finally {
      setRevokeAllBusy(false);
    }
  }, [user?.id, fetchSessions, toast]);

  // ---- Render --------------------------------------------------------------
  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl space-y-6">
      <div className="flex items-center gap-3 mb-2">
        <Shield className="h-7 w-7 text-purple-600 dark:text-purple-400" />
        <div>
          <h1 className="text-2xl font-bold text-foreground">Sicurezza account</h1>
          <p className="text-sm text-muted-foreground">
            Gestisci 2FA, password e sessioni del tuo account amministratore master.
          </p>
        </div>
      </div>

      {/* ===== 2FA ===== */}
      <GlassCard>
        <GlassCardHeader>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <GlassCardTitle className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                Autenticazione a due fattori (TOTP)
              </GlassCardTitle>
              <GlassCardDescription>
                Aggiungi un codice temporaneo al login. Obbligatorio per ambienti enterprise.
              </GlassCardDescription>
            </div>
            {loadingStatus ? (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            ) : (
              <TwoFactorStatusBadge enabled={Boolean(meStatus?.totpEnabled)} />
            )}
          </div>
        </GlassCardHeader>
        <GlassCardContent className="space-y-4">
          {twoFactorRequired && !meStatus?.totpEnabled && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                Il 2FA è obbligatorio per gli amministratori master. Configuralo ora: al prossimo
                accesso ti verrà richiesto.
              </AlertDescription>
            </Alert>
          )}
          {!setupData && !meStatus?.totpEnabled && (
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
              <Alert className="flex-1">
                <ShieldOff className="h-4 w-4" />
                <AlertDescription>
                  2FA non attivo. Configuralo con un'app TOTP (Google Authenticator, Authy, 1Password).
                </AlertDescription>
              </Alert>
              <Button onClick={() => { void startSetup(); }} disabled={wizardBusy}>
                {wizardBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
                Avvia configurazione
              </Button>
            </div>
          )}

          {setupData && (
            <div className="space-y-5">
              {/* Step indicator */}
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                {(['Scansione QR', 'Codice di conferma', 'Backup codes'] as const).map((label, i) => (
                  <React.Fragment key={label}>
                    {i > 0 && <ArrowRight className="h-3 w-3" />}
                    <span
                      className={
                        wizardStep === i
                          ? 'font-semibold text-purple-600 dark:text-purple-400'
                          : wizardStep > i
                          ? 'text-foreground'
                          : ''
                      }
                    >
                      {i + 1}. {label}
                    </span>
                  </React.Fragment>
                ))}
              </div>

              {wizardStep === 0 && (
                <div className="space-y-4">
                  <TotpQrSetup
                    qrCodeDataUri={setupData.qrCodeDataUri}
                    manualEntryKey={setupData.manualEntryKey}
                  />
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" onClick={cancelWizard}>Annulla</Button>
                    <Button onClick={() => setWizardStep(1)}>
                      Ho scansionato il QR
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </div>
                </div>
              )}

              {wizardStep === 1 && (
                <div className="space-y-4">
                  <p className="text-sm text-foreground">
                    Inserisci il codice a 6 cifre mostrato dall'app per confermare la configurazione.
                  </p>
                  <TotpCodeInput
                    value={wizardCode}
                    onChange={(v) => { setWizardCode(v); setWizardError(null); }}
                    onComplete={(code) => { void confirmEnable(code); }}
                    autoFocus
                    disabled={wizardBusy}
                    error={wizardError}
                  />
                  <div className="flex justify-between gap-2">
                    <Button variant="ghost" onClick={() => setWizardStep(0)} disabled={wizardBusy}>
                      Indietro
                    </Button>
                    <Button
                      onClick={() => { void confirmEnable(wizardCode); }}
                      disabled={wizardBusy || wizardCode.replace(/\s/g, '').length !== 6}
                    >
                      {wizardBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
                      Conferma e attiva
                    </Button>
                  </div>
                </div>
              )}

              {wizardStep === 2 && (
                <div className="space-y-4">
                  <BackupCodesViewer
                    codes={setupData.backupCodes}
                    requireAck
                    acknowledged={wizardAck}
                    onAcknowledgeChange={setWizardAck}
                  />
                  <div className="flex justify-end">
                    <Button onClick={finishWizard} disabled={!wizardAck}>
                      <Check className="h-4 w-4 mr-2" />
                      Fine
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {!setupData && meStatus?.totpEnabled && (
            <div className="flex flex-col sm:flex-row gap-2">
              <Button variant="outline" onClick={() => setRegenOpen(true)}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Rigenera backup codes
              </Button>
              <Button
                variant="outline"
                onClick={() => setDisableOpen(true)}
                className="text-red-600 border-red-300 hover:bg-red-50 dark:hover:bg-red-500/10"
              >
                <ShieldOff className="h-4 w-4 mr-2" />
                Disattiva 2FA
              </Button>
            </div>
          )}
        </GlassCardContent>
      </GlassCard>

      {/* ===== Password ===== */}
      <GlassCard>
        <GlassCardHeader>
          <GlassCardTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            Cambio password
          </GlassCardTitle>
          <GlassCardDescription>
            Cambia la tua password di accesso. La nuova password invaliderà tutte le altre sessioni attive.
          </GlassCardDescription>
        </GlassCardHeader>
        <GlassCardContent className="space-y-4">
          {pwdSuccess && (
            <Alert className="border-green-300 bg-green-50 dark:bg-green-500/10 dark:border-green-500/30">
              <Check className="h-4 w-4" />
              <AlertDescription>
                Password aggiornata. Le altre sessioni attive sono state invalidate.
              </AlertDescription>
            </Alert>
          )}
          <div className="space-y-2">
            <Label htmlFor="curPwd">Password corrente</Label>
            <Input
              id="curPwd"
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => { setCurrentPassword(e.target.value); setPwdError(null); setPwdSuccess(false); }}
              placeholder="La tua password attuale"
              disabled={pwdBusy}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="newPwd">Nuova password</Label>
              <Input
                id="newPwd"
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => { setNewPassword(e.target.value); setPwdError(null); setPwdSuccess(false); }}
                placeholder="Min 8 + maiuscola, minuscola, numero, simbolo"
                disabled={pwdBusy}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPwd">Conferma nuova password</Label>
              <Input
                id="confirmPwd"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setPwdError(null); }}
                placeholder="Ripeti la nuova password"
                disabled={pwdBusy}
              />
            </div>
          </div>
          {newPassword.length > 0 && (
            <div className="space-y-1">
              <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                <div className={`h-full transition-all duration-200 ${pwdStrength.className}`} />
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Forza: {pwdStrength.label}</span>
                {pwdMismatch && (
                  <span className="text-red-600 dark:text-red-400">Le password non coincidono</span>
                )}
              </div>
            </div>
          )}
          {pwdError && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{pwdError}</AlertDescription>
            </Alert>
          )}
          <div className="flex flex-col sm:flex-row gap-2 sm:justify-between sm:items-center">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => { void requestPasswordReset(); }}
              disabled={pwdResetEmailBusy || pwdResetEmailSent || !meStatus?.email}
              className="text-xs"
            >
              {pwdResetEmailBusy
                ? <Loader2 className="h-3 w-3 mr-2 animate-spin" />
                : <Mail className="h-3 w-3 mr-2" />}
              {pwdResetEmailSent ? 'Email di reset inviata' : 'Password dimenticata? Ricevi link via email'}
            </Button>
            <div className="flex justify-end">
              <Button
                onClick={() => { void submitPasswordChange(); }}
                disabled={
                  pwdBusy
                  || currentPassword.length === 0
                  || !pwdPolicyOk
                  || pwdMismatch
                  || confirmPassword.length === 0
                }
              >
                {pwdBusy
                  ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  : <KeyRound className="h-4 w-4 mr-2" />}
                Aggiorna password
              </Button>
            </div>
          </div>
        </GlassCardContent>
      </GlassCard>

      {/* ===== Sessions ===== */}
      <GlassCard>
        <GlassCardHeader>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <GlassCardTitle className="flex items-center gap-2">
                <LogOut className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                Sessioni attive
              </GlassCardTitle>
              <GlassCardDescription>
                Refresh token attivi sul tuo account. Revoca quelli che non riconosci.
              </GlassCardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { void revokeAll(); }}
              disabled={revokeAllBusy || sessions.length === 0}
              className="text-red-600 border-red-300 hover:bg-red-50 dark:hover:bg-red-500/10"
            >
              {revokeAllBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <LogOut className="h-4 w-4 mr-2" />}
              Revoca tutte
            </Button>
          </div>
        </GlassCardHeader>
        <GlassCardContent>
          {loadingSessions ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : sessions.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">Nessuna sessione attiva.</p>
          ) : (
            <div className="space-y-2">
              {sessions.map((s) => (
                <SessionRow
                  key={s.id}
                  session={{
                    id: s.id,
                    ip_address: s.ip_address,
                    user_agent: s.user_agent,
                    device_info: s.device_info,
                    created_at: s.created_at,
                    expires_at: s.expires_at,
                    revoked: s.revoked,
                  }}
                  revoking={revokingId === s.id}
                  canRevoke
                  onRevoke={(id) => { void revokeSession(id); }}
                />
              ))}
            </div>
          )}
        </GlassCardContent>
      </GlassCard>

      {/* ===== Dialog: disattiva 2FA ===== */}
      <Dialog open={disableOpen} onOpenChange={(o) => { if (!o) { setDisableOpen(false); setDisableError(null); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldOff className="h-5 w-5 text-red-600" />
              Disattiva 2FA
            </DialogTitle>
            <DialogDescription>
              Conferma con password attuale e codice TOTP corrente. Difesa in profondità: serve sia conoscenza sia possesso.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-2">
              <Label htmlFor="disPwd">Password attuale</Label>
              <Input
                id="disPwd"
                type="password"
                autoComplete="current-password"
                value={disablePassword}
                onChange={(e) => setDisablePassword(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Codice TOTP corrente</Label>
              <TotpCodeInput
                value={disableCode}
                onChange={setDisableCode}
                disabled={disableBusy}
                error={disableError}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDisableOpen(false)} disabled={disableBusy}>Annulla</Button>
            <Button
              onClick={() => { void submitDisable(); }}
              disabled={disableBusy}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {disableBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldOff className="h-4 w-4 mr-2" />}
              Disattiva 2FA
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== Dialog: rigenera backup codes ===== */}
      <Dialog open={regenOpen} onOpenChange={(o) => { if (!o) closeRegen(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5 text-purple-600" />
              Rigenera backup codes
            </DialogTitle>
            <DialogDescription>
              I codici precedenti diventeranno immediatamente invalidi. Inserisci il codice TOTP corrente per confermare.
            </DialogDescription>
          </DialogHeader>
          {regenResult ? (
            <div className="space-y-3 py-2">
              <BackupCodesViewer codes={regenResult} />
            </div>
          ) : (
            <div className="space-y-3 py-2">
              <TotpCodeInput
                value={regenCode}
                onChange={setRegenCode}
                disabled={regenBusy}
                error={regenError}
                autoFocus
                onComplete={() => { void submitRegenerate(); }}
              />
            </div>
          )}
          <DialogFooter>
            {regenResult ? (
              <Button onClick={closeRegen}>
                <Check className="h-4 w-4 mr-2" />
                Chiudi
              </Button>
            ) : (
              <>
                <Button variant="ghost" onClick={closeRegen} disabled={regenBusy}>Annulla</Button>
                <Button
                  onClick={() => { void submitRegenerate(); }}
                  disabled={regenBusy || regenCode.replace(/\s/g, '').length !== 6}
                >
                  {regenBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                  Rigenera
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
